import { Controller } from "@hotwired/stimulus"
import { apiJson, formatApiErrorDetail } from "../lib/api_errore"

// La sezione «Opportunità dalle fonti» del passo 2 (pages/Opportunita.js di 31a5bd1, delta 07 B21; trova/_opportunita).
// Come l'originale tre letture indipendenti, ognuna mostrata quando arriva: la ricerca nelle fonti (il frame della
// sezione, POST /app/trova/opportunita), la data dell'export (GET /api/opportunita, un errore la nasconde e basta) e le
// richieste dell'organizzazione (GET /app/trova/richieste: finché non arrivano «Richiedi verifica» resta spento). Un
// solo avviso: lo scrive l'ultima lettura non riuscita, come setErrore nell'originale. Poi: le pagine da 20 proposte
// (nella pagina solo quelle della pagina corrente), «Richiedi verifica» (POST /api/opportunita/:id/ottieni con il
// profilo della proposta), che mette la richiesta in cima all'elenco e mostra la conferma senza simulare una
// validazione, «Già richiesto» per la proposta con la richiesta dello stesso lavorazione_id, e la rilettura delle
// richieste ogni 30 s finché ce n'è una non verificata. Una rilettura non riuscita (anche un rinvio all'accesso o una
// sessione scaduta) lo dice e tiene l'ultimo stato. Una sezione uscita dalla pagina (nuova ricerca) non legge più nulla.
const PER_PAGINA = 20
const CONFERMA = "Richiesta registrata con la fonte. Segui l’acquisizione qui sotto; l’amministratore completa la validazione del bando."
const ERRORE_RICHIESTA = "Richiesta non completata: controlla lo stato prima di riprovare."
const ERRORE_STATO = "Stato delle richieste non disponibile: ricarica prima di aggiungere un bando."
const ERRORE_RILETTURA = "Aggiornamento richieste non riuscito: l’ultimo stato potrebbe essere superato."

export default class extends Controller {
  static targets = ["fonte", "acquisito", "attesa", "errore", "conferma", "confermaTesto", "frame", "richieste", "riga",
    "modello", "ancora", "totale", "precedenti", "successivi", "numero"]
  static values = { stati: Object, intervallo: { type: Number, default: 30000 } }

  connect() {
    this.pagina = 0
    this.busy = false
    this.pronte = false
    this.attivo = true
    apiJson("/api/opportunita").then((data) => {
      if (!this.attivo || !data) return
      this.acquisitoTarget.textContent = data.acquisito_il ?? ""
      this.fonteTarget.hidden = false
    }).catch(() => {})
    this.leggiRichieste(ERRORE_STATO)
  }

  disconnect() {
    this.attivo = false
    this.ferma()
  }

  intervalloValueChanged() {
    if (this.attivo && this.pronte) this.programma()
  }

  // ---- la ricerca nelle fonti (il frame) ------------------------------------------------------------------------

  risultatiArrivati() {
    if (!this.attivo) return
    this.attesaTarget.hidden = true
    this.frameTarget.hidden = false
    const errore = this.frameTarget.querySelector("[data-opportunita-errore]")?.dataset.opportunitaErrore
    if (errore) this.mostraErrore(errore)
    this.pagina = 0
    this.disegnaPagina()
  }

  totale() {
    return this.hasTotaleTarget ? Number(this.totaleTarget.dataset.totale) : 0
  }

  // risultati.schede.slice(pagina * 20, (pagina + 1) * 20): nella pagina solo le proposte della pagina corrente
  disegnaPagina() {
    if (!this.hasAncoraTarget) return
    this.frameTarget.querySelectorAll("[data-scheda-viva]").forEach((s) => s.remove())
    const modelli = this.modelloTargets.slice(this.pagina * PER_PAGINA, (this.pagina + 1) * PER_PAGINA)
    this.ancoraTarget.before(...modelli.map((t) => t.content.firstElementChild.cloneNode(true)))
    if (this.hasNumeroTarget) {
      this.numeroTarget.textContent = `${this.pagina + 1} / ${Math.ceil(this.totale() / PER_PAGINA)}`
      this.precedentiTarget.disabled = this.pagina === 0
      this.successiviTarget.disabled = (this.pagina + 1) * PER_PAGINA >= this.totale()
    }
    this.sincronizza()
  }

  precedenti() {
    this.pagina -= 1
    this.disegnaPagina()
  }

  successivi() {
    this.pagina += 1
    this.disegnaPagina()
  }

  // ---- le richieste ---------------------------------------------------------------------------------------------

  righe() {
    return [...this.richiesteTarget.querySelectorAll(":scope > article[data-richiesta-id]")]
  }

  // GET /app/trova/richieste (righe in HTML). Solo una risposta 2xx, HTML, non rinviata e con il segno della rilettura
  // vale: una pagina d'accesso o un 401 sono un errore, e le righe restano quelle di prima.
  async leggiRichieste(erroreSe) {
    try {
      const res = await fetch("/app/trova/richieste", { credentials: "same-origin", headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" } })
      if (!res.ok || res.redirected || !(res.headers.get("content-type") || "").includes("text/html")) throw new Error(`HTTP ${res.status}`)
      const t = document.createElement("template")
      t.innerHTML = await res.text()
      if (!t.content.querySelector("[data-richieste-ok]")) throw new Error("risposta senza richieste")
      if (!this.attivo) return
      this.righe().forEach((r) => r.remove())
      this.richiesteTarget.append(...[...t.content.children].filter((el) => el.matches("article[data-richiesta-id]")))
      this.pronte = true
      this.sincronizza()
      this.programma()
    } catch {
      if (this.attivo) this.mostraErrore(erroreSe)
    }
  }

  // richieste.some(r => !r.verificato): finché ce n'è una, si rileggono ogni 30 s
  programma() {
    this.ferma()
    if (!this.righe().some((r) => r.dataset.verificato !== "true")) return
    this.timer = setInterval(() => this.leggiRichieste(ERRORE_RILETTURA), this.intervalloValue)
    this.element.dataset.rilettura = "attiva"
  }

  ferma() {
    clearInterval(this.timer)
    delete this.element.dataset.rilettura
  }

  mostraErrore(testo) {
    this.erroreTarget.textContent = testo
    this.erroreTarget.hidden = !testo
  }

  mostraConferma(si) {
    this.confermaTestoTarget.textContent = si ? CONFERMA : ""
    this.confermaTarget.hidden = !si
  }

  // «Già richiesto» e lo stato per la proposta che ha una richiesta con lo stesso lavorazione_id
  sincronizza() {
    const perId = Object.fromEntries(this.righe().map((r) => [r.dataset.richiestaId, r]))
    this.frameTarget.querySelectorAll("[data-scheda-viva]").forEach((s) => {
      const riga = perId[s.dataset.lavorazioneId]
      const b = s.querySelector("[data-richiedi]")
      const stato = s.querySelector("[data-stato-richiesta]")
      b.textContent = riga ? "Già richiesto" : "Richiedi verifica"
      b.disabled = this.busy || !this.pronte || Boolean(riga)
      stato.hidden = !riga
      stato.textContent = riga ? riga.dataset.statoLabel : ""
    })
  }

  async richiedi(event) {
    const b = event.currentTarget
    this.busy = true
    this.mostraErrore("")
    this.mostraConferma(false)
    this.sincronizza()
    let profilo = null
    try { profilo = JSON.parse(b.dataset.profilo || "null") } catch { profilo = null }
    try {
      const data = await apiJson(`/api/opportunita/${encodeURIComponent(b.dataset.cid)}/ottieni`, { method: "POST", body: { profilo } })
      if (!this.attivo) return
      this.righe().filter((r) => r.dataset.richiestaId === String(data.id)).forEach((r) => r.remove())
      this.richiesteTarget.querySelector(":scope > h3").after(this.riga(data))
      this.mostraConferma(true)
    } catch (e) {
      if (this.attivo) this.mostraErrore(formatApiErrorDetail(e.detail) || ERRORE_RICHIESTA)
    } finally {
      this.busy = false
      if (this.attivo) { this.sincronizza(); this.programma() }
    }
  }

  // la riga di una richiesta appena registrata (trova/_richiesta), con i suoi testi
  riga(r) {
    const el = this.rigaTarget.content.firstElementChild.cloneNode(true)
    const stato = this.statiValue[r.stato] || "Stato da verificare"
    el.dataset.richiestaId = String(r.id)
    el.dataset.verificato = r.verificato ? "true" : "false"
    el.dataset.statoLabel = stato
    el.querySelector("[data-campo='titolo']").textContent = r.titolo ?? ""
    el.querySelector("[data-campo='stato']").textContent = stato
    el.querySelector("[data-campo='messaggio']").textContent = r.messaggio ?? ""
    el.querySelector("[data-campo='verificato']").hidden = !r.verificato
    return el
  }
}
